'use client';

import { useTranslations } from 'next-intl';
import { ReactNode, useState } from 'react';

import ArrowLeftIcon from '@/fo/icons/base/icons/arrow24.svg';
import {
  UiArticleNavigationBase,
  Navigation
} from '@/fo/ui/article/navigation/base';
import { UiKitBannerHeroProps } from '@/fo/ui/kit/banner/hero';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitTypographyBase } from '@/fo/ui/kit/typography/base';
import { UiModStickyScroll } from '@/fo/ui/mod/sticky/scroll';

import { TableOfContents } from '../components/TableOfContents/TableOfContents';
import { TableOfContentsItem } from '../types';

import { styles } from './UiArticleLayoutPage.styles';

export interface UiArticleLayoutPageProps {
  content?: ReactNode | string;
  navigation?: Navigation;
  title?: string;
  subtitle?: string;
  description?: ReactNode | string;
  tableOfContents?: TableOfContentsItem[];
  banners: React.ReactNode;
  breadcrumbs: UiKitBannerHeroProps['breadcrumbs'];
  children?: ReactNode;
  hideMobileBanner?: boolean;
}

export function UiArticleLayoutPage({
  content,
  title,
  subtitle,
  navigation,
  tableOfContents,
  description,
  banners,
  children,
  hideMobileBanner = false
}: UiArticleLayoutPageProps) {
  const [isOpenMenu, setIsOpenMenu] = useState(false);

  const t = useTranslations();

  const handleOpen = () => {
    setIsOpenMenu(true);
  };

  const handleActiveClick = () => {
    setIsOpenMenu(false);
  };

  const slots = styles({ openMenu: isOpenMenu });

  return (
    <>
      <div className={slots.banner({ hideMobileBanner })}>{banners}</div>
      <div className={slots.base()}>
        {!!navigation && (
          <div className={slots.menu()}>
            <UiModStickyScroll className={slots.menuInner()}>
              <UiArticleNavigationBase
                navigation={navigation}
                onActiveClick={handleActiveClick}
              />
            </UiModStickyScroll>
          </div>
        )}
        <div
          className={slots.back()}
          data-mobile-app-hidden="breadcrumbs"
        >
          <UiKitButtonBase
            color="ghost"
            onClick={handleOpen}
            className={slots.backButton()}
          >
            <ArrowLeftIcon className={slots.backIcon()} />
            <span className={slots.backText()}>{t('back')}</span>
          </UiKitButtonBase>
        </div>
        <div className={slots.contentContainer()}>
          {title && <h1 className={slots.title()}>{title}</h1>}
          {subtitle && <p className={slots.subtitle()}>{subtitle}</p>}
          {description && (
            <UiKitTypographyBase
              as="div"
              className={slots.description()}
            >
              {description}
            </UiKitTypographyBase>
          )}
          {tableOfContents && tableOfContents.length > 0 && (
            <TableOfContents
              className={slots.tableOfContents()}
              items={tableOfContents}
            />
          )}
          {content && (
            <UiKitTypographyBase className={slots.content()}>
              {content}
            </UiKitTypographyBase>
          )}
          {children}
        </div>
      </div>
    </>
  );
}
